import { useEffect, useRef, useState } from "react"
import { usePropertyAutocomplete } from "@/queries/analysis"
import {
  BFTextInput,
  BFTextInputContainer,
} from "@/components/standard/BFTextInput"
// eslint-disable-next-line no-restricted-imports
import { Platform, type TextInput, TouchableOpacity } from "react-native"
import { Vertical } from "@/components/standard/BFLayout"
import { Body, Small } from "@/components/standard/BFText"
import * as Location from "expo-location"
import PlaceIcon from "@/assets/icons/place.svg"
import { Path, Svg, type SvgProps } from "react-native-svg"

interface PlaceProps {
  place?: {
    address: string
    placeId?: string | null
    types?: Array<string | null> | null
  } | null
  onSelect: (address: string, placeId?: string | null) => void
}

export function LightSearchIcon(props: SvgProps) {
  return (
    <Svg width={16} height={17} viewBox="0 0 16 17" fill="none" {...props}>
      <Path
        d="M15.82 15.67l-3.848-3.846a6.77 6.77 0 10-.869.87l3.847 3.847a.614.614 0 10.87-.87zM1.246 7.5a5.532 5.532 0 115.533 5.532A5.539 5.539 0 011.246 7.5z"
        fill="#A7A4A4"
      />
    </Svg>
  )
}

const AddressView = ({ address }: { address: string }) => {
  const firstIndex = address?.indexOf(", ") || -1
  let street: string = address || ""
  let stateCityZip: string = ""
  if (firstIndex >= 0) {
    street = address?.substring(0, firstIndex) || ""
    stateCityZip = address?.substring(firstIndex + 2) || ""
  }

  return (
    <Vertical className={"p-3"}>
      <Body className={"text-midnight-blue "}>{street}</Body>
      <Small className={"text-warm-grey"}>{stateCityZip}</Small>
    </Vertical>
  )
}

const Place = ({ place, onSelect }: PlaceProps) => {
  if (!place) {
    return null
  }

  return (
    <TouchableOpacity
      testID={`place-${place?.placeId}`}
      onPress={() => {
        if (place?.address) {
          onSelect(place.address, place.placeId)
        }
      }}
      className={"flex-row items-center gap-1"}
    >
      <PlaceIcon />
      <AddressView address={place.address} />
    </TouchableOpacity>
  )
}

export const AddressSelector = ({
  onSelect,
}: {
  onSelect: (address: string | undefined, placeId: string | undefined) => void
}) => {
  const [location, setLocation] = useState<string | null>(null)
  const [address, setAddress] = useState("")
  const { data: propertyAutocomplete } = usePropertyAutocomplete(
    address,
    location
  )
  const [selectedAddress, setSelectedAddress] = useState<string | undefined>()
  const textInput = useRef<TextInput | null>(null)

  function onSelectPlace(selectedAddress: string) {
    setSelectedAddress(selectedAddress)
  }

  useEffect(() => {
    void (async () => {
      const { status } = await Location.requestForegroundPermissionsAsync()
      if (status !== "granted") {
        console.log("Permission to access location was denied")
        return
      }

      try {
        const location =
          (Platform.OS !== "web" &&
            (await Location.getLastKnownPositionAsync())) ||
          (await Location.getCurrentPositionAsync())
        setLocation(`${location.coords.latitude},${location.coords.longitude}`)
      } catch (err) {
        console.warn(err)
      }
    })()
  }, [])

  return (
    <Vertical>
      {selectedAddress ? (
        <BFTextInputContainer
          variant={"form"}
          accessoryLeft={<PlaceIcon />}
          accessoryRight={
            <TouchableOpacity
              testID={"edit-address"}
              hitSlop={16}
              onPress={() => {
                setSelectedAddress(undefined)
              }}
            >
              <Small className={"underline text-calm-blue px-2"}>Edit</Small>
            </TouchableOpacity>
          }
        >
          <AddressView address={selectedAddress} />
        </BFTextInputContainer>
      ) : (
        <>
          <BFTextInput
            ref={textInput}
            testID={"address-selector"}
            variant={"form"}
            keyboardType={"default"}
            returnKeyType={"done"}
            onChangeText={setAddress}
            value={address}
            placeholder={"Address Search"}
            accessoryRight={<LightSearchIcon />}
          />
          {propertyAutocomplete?.googlePlaces?.map((place) => (
            <Place
              key={place?.placeId}
              place={place}
              onSelect={() => {
                onSelectPlace(place?.address!)
                onSelect?.(place?.address, place?.placeId || undefined)
              }}
            />
          ))}
        </>
      )}
    </Vertical>
  )
}
